<template>
  <div class="facturation-fenetre" role="dialog" aria-modal="true" aria-labelledby="facturation-titre">
    <form class="facturation-fenetre-contenu" data-dictee="non" novalidate @submit.prevent="enregistrer">
      <div class="facturation-entete">
        <div>
          <h4 id="facturation-titre" class="mb-0">Facturation</h4>
          <span class="text-grey">{{ nom }}</span>
        </div>
        <button type="button" class="btn btn-link facturation-fermer" aria-label="Fermer la facturation" :disabled="enregistrement" @click="$emit('fermer')">×</button>
      </div>
      <div v-if="chargement" class="text-grey">Chargement…</div>
      <template v-else>
        <div class="facturation-corps">
        <h5 class="facturation-section-titre">Références et prise en charge</h5>
        <div class="form-row">
          <div class="form-group col-md-6"><label for="facturation-prefixe">Préfixe réf. facture *</label>
            <input id="facturation-prefixe" v-model.trim="f.prefixe" class="input-secondary-light" maxlength="50">
            <small class="text-grey">Préfixe des factures adressées à cette société.</small></div>
          <div class="form-group col-md-6"><span class="d-block mb-1">N° de bon obligatoire</span>
            <div class="facturation-choix" role="group" aria-label="Numéro de bon obligatoire">
              <label><input v-model.number="f.num_bon_is_required" type="radio" name="facturation-bon" :value="1"> Oui</label>
              <label><input v-model.number="f.num_bon_is_required" type="radio" name="facturation-bon" :value="0"> Non</label>
            </div>
            <small class="text-grey">Bon délivré par la société, avec date d’expiration.</small></div>
        </div>
        <div class="form-group facturation-mode"><div><span class="d-block mb-1">Modèle forfaitaire</span>
          <small class="text-grey">Facturation de la société au forfait.</small></div>
          <div class="facturation-choix" role="group" aria-label="Modèle forfaitaire">
            <label><input v-model.number="f.a_model_forfait" type="radio" name="facturation-forfait" :value="1"> Oui</label>
            <label><input v-model.number="f.a_model_forfait" type="radio" name="facturation-forfait" :value="0"> Non</label>
          </div>
        </div>
        <h5 class="facturation-section-titre">Tarif et couverture</h5>
        <div class="form-row">
          <div class="form-group col-md-4"><label for="facturation-pu">Prix unitaire du forfait *</label>
            <div class="input-container"><input id="facturation-pu" v-model.trim="f.prix_unitaire_forfait" class="input-secondary-light" inputmode="numeric">
              <span class="input-icon ic-secondary font-wgh-900">{{ monnaie }}</span></div></div>
          <div class="form-group col-md-4"><label for="facturation-qte">Quantité *</label>
            <input id="facturation-qte" v-model.trim="f.qte_forfait" class="input-secondary-light" inputmode="numeric"></div>
          <div class="form-group col-md-4"><span class="d-block mb-1">Prix total</span>
            <strong class="facturation-total">{{ total === null ? '—' : nombre(total) + ' ' + monnaie }}</strong></div>
        </div>
        <div class="form-group facturation-duree"><label for="facturation-duree">Durée de couverture *</label>
          <div class="input-container"><input id="facturation-duree" v-model.trim="f.duree_forfait" class="input-secondary-light" inputmode="numeric">
            <span class="input-icon ic-secondary font-wgh-900">jours</span></div>
          <small class="text-grey">Au-delà de cette durée d’hospitalisation, les exclusions après couverture s’appliquent.</small></div>
        <p v-if="erreur" class="text-danger" role="alert">{{ erreur }}</p>
        </div>
        <div class="ui-modal-actions facturation-actions">
          <button type="button" class="btn btn-secondary-light-o btn-radius" :disabled="enregistrement" @click="$emit('fermer')">Annuler</button>
          <button type="submit" class="btn btn-primary btn-radius ml-2" :disabled="enregistrement">{{ enregistrement ? 'Enregistrement…' : 'Enregistrer' }}</button>
        </div>
      </template>
    </form>
  </div>
</template>

<script>
import ConventionsApi from '@/api/parametres/_conventions'
import { formulaireFacturation, entierPositif, montantTotal, nombre } from './facturation-utils'

// Facturation d'un contrat : mêmes champs, mêmes règles et même enregistrement que la fenêtre « Editer »
// de Caisse › Paramètres › Sociétés, plus le « modèle forfaitaire » de ce contrat.
export default {
  name: 'facturation-contrat',
  props: {
    souscripteurId: { type: [String, Number], required: true },
    nom: { type: String, default: '' },
  },
  data() {
    return { chargement: true, f: formulaireFacturation(null), erreur: '', enregistrement: false }
  },
  computed: {
    total() {
      return montantTotal(this.f.prix_unitaire_forfait, this.f.qte_forfait)
    },
    monnaie() {
      return this.currency_info && this.currency_info.principal_currency ? this.currency_info.principal_currency : ''
    },
  },
  mounted() {
    this.charger()
  },
  methods: {
    nombre,
    charger() {
      this.chargement = true
      return ConventionsApi.infoSociete(this.souscripteurId).then(reponse => {
        this.f = formulaireFacturation(reponse && reponse.data)
      }).catch(() => {
        this.erreur = 'La facturation du contrat n’a pas pu être chargée.'
      }).then(() => {
        this.chargement = false
      })
    },
    enregistrer() {
      if (this.enregistrement) return
      const f = this.f
      if (!f.prefixe || !f.duree_forfait || f.prix_unitaire_forfait === '' || f.qte_forfait === '') {
        this.erreur = 'Renseignez le préfixe, la durée de couverture, le prix unitaire et la quantité.'
        return
      }
      if (![f.duree_forfait, f.prix_unitaire_forfait, f.qte_forfait].every(entierPositif)) {
        this.erreur = 'La durée, le prix unitaire et la quantité sont des nombres entiers.'
        return
      }
      this.erreur = ''
      this.enregistrement = true
      return ConventionsApi.enregistrerInfoSociete({
        souscripteur_id: this.souscripteurId,
        prefixe: f.prefixe,
        prix_unitaire_forfait: parseInt(f.prix_unitaire_forfait, 10),
        qte_forfait: parseInt(f.qte_forfait, 10),
        duree_forfait: parseInt(f.duree_forfait, 10),
        num_bon_is_required: f.num_bon_is_required,
        a_model_forfait: f.a_model_forfait,
      }).then(reponse => {
        if (reponse && reponse.success) {
          if (this.$toasted) this.$toasted.show('Facturation enregistrée', { duration: 3000, theme: 'toasted-primary', type: 'success', position: 'top-center' })
          this.$emit('enregistre')
          return
        }
        this.erreur = (reponse && reponse.message) || 'L’enregistrement a échoué.'
      }).catch(() => {
        this.erreur = 'Une erreur est survenue lors de l’enregistrement. Veuillez réessayer plus tard.'
      }).then(() => {
        this.enregistrement = false
      })
    },
  },
}
</script>

<style scoped>
.facturation-fenetre { position: fixed; z-index: 1060; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(87, 104, 131, .32); backdrop-filter: blur(3px); padding: 16px }
.facturation-fenetre-contenu { display: flex; flex-direction: column; background: #fff; border: 1px solid #dce4f2; border-radius: 20px; width: 100%; max-width: 680px; max-height: calc(100vh - 32px); overflow: hidden; padding: 0; text-align: left; color: var(--ui-text, #172033); font-size: 14px; box-shadow: 0 18px 60px rgba(39,52,73,.15) }
.facturation-corps { padding: 18px 22px 6px; overflow-y: auto; min-height: 0 }
.facturation-corps .form-group { margin-bottom: 14px }
.facturation-corps .form-group > label, .facturation-corps .form-group > span, .facturation-mode > div > span { font-size: 13px; font-weight: 600; margin-bottom: 6px; color: var(--ui-label, #273449) }
.facturation-corps small { display: block; font-size: 12px; line-height: 1.5; margin-top: 5px; color: var(--ui-text-muted, #526074) }
.facturation-corps .input-secondary-light { width: 100%; height: 42px; min-height: 42px; border: 1px solid #9aabc7; border-radius: 10px; padding: 8px 12px; font-size: 14px; line-height: 1.4; color: var(--ui-text, #172033) }
.facturation-corps .input-container { position: relative }
.facturation-corps .input-container input { padding-right: 70px }
.facturation-corps .input-icon { position: absolute; top: 50%; right: 12px; transform: translateY(-50%); font-size: 12px; color: var(--ui-text-muted, #526074); pointer-events: none }
.facturation-section-titre { margin: 0 0 12px; font-size: 12px; font-weight: 700; color: #526074 }
.facturation-fenetre-contenu .form-group label { display: block }
.facturation-entete { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 16px 22px; border-bottom: 1px solid #e3e9f4; flex-shrink: 0 }
.facturation-entete h4 { font-size: 20px; font-weight: 700; line-height: 1.3 }
.facturation-entete span { display: block; margin-top: 3px; font-size: 13px; color: #526074 }
.facturation-fermer { font-size: 24px; line-height: 1; min-width: 36px; min-height: 36px; padding: 6px }
.facturation-fenetre-contenu .facturation-choix { display: inline-flex; gap: 6px; }
.facturation-fenetre-contenu .facturation-choix label { display: inline-flex; align-items: center; gap: 7px; margin: 0; border: 1px solid #d6dff2; border-radius: 9px; padding: 8px 12px; font-size: 14px; cursor: pointer }
.facturation-choix input { accent-color: #2457c5; width: 16px; height: 16px; margin: 0 }
.facturation-mode { display: flex; align-items: center; justify-content: space-between; gap: 12px; background: #f5f8fc; border: 1px solid #e3e9f4; border-radius: 12px; padding: 12px; }
.facturation-mode small { margin: 0 }
.facturation-total { display: block; padding: 9px 12px; border-radius: 10px; background: #edf3ff; font-size: 17px; min-height: 42px; color: var(--ui-text, #172033) }
.facturation-duree { max-width: none }
.facturation-duree .input-container { max-width: 220px }
.facturation-actions { border-top: 1px solid #e3e9f4; padding: 12px 22px; flex-shrink: 0 }
.facturation-corps input:focus-visible, .facturation-fermer:focus-visible { outline: 2px solid #2457c5; outline-offset: 2px }
@media (max-width: 600px) { .facturation-corps { padding: 14px 16px 2px } .facturation-entete { padding: 14px 16px } .facturation-mode { flex-wrap: wrap } .facturation-actions { padding: 12px 16px } }
</style>
